// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { useAuth } from "../auth/AuthContext";
import {
  abrirTicket,
  asignarTicket,
  comentarTicket,
  moverTicket,
  priorizarTicket,
  responderAlChofer,
  traerAreas,
  traerHilo,
  traerTicket,
  traerTickets,
  traerMiArea,
} from "../services/tickets";
import { laVeo } from "../shared/ticketsDerivados";
import { choferesDeAvisos } from "../services/espacio";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import WhatsappChofer from "../components/funcionales/WhatsappChofer";
import Loading from "./Loading";
import { diaAr } from "../shared/fechaAr";
import { avisoDelTicket } from "../shared/tareaDerivada";
import { esChofer, puedeVerTelefono, whatsappDeChofer } from "../shared/telefono";

/* La ticketera.
 *
 * Reemplaza el lugar donde esto pasa hoy, que es WhatsApp: ahí no se puede
 * buscar, no se sabe si alguien lo tomó, y lo que se resolvió se pierde igual
 * que lo que no. Un ticket sirve para tres cosas y ninguna más — que quede
 * escrito, que tenga dueño, y que se pueda leer después qué se hizo.
 *
 * Dos columnas, como en Permisos: la lista a la izquierda y el ticket abierto a
 * la derecha. Con una sola columna hay que ir y volver para pasar de uno a
 * otro, y en un tablero uno hace exactamente eso todo el tiempo.
 *
 * Abrir un ticket no pide ningún permiso: cualquiera con sesión puede pedir
 * algo. Ver lo ajeno y atender sí, y se apoyan en los permisos que ya están
 * repartidos — no hay un "ver.tickets" que haya que ir a dar de nuevo a
 * cuarenta personas antes de que esto sirva.
 */

const ESTADOS = [
  { clave: "abierto", et: "Abierto" },
  { clave: "en_curso", et: "En curso" },
  { clave: "esperando", et: "Esperando" },
  { clave: "resuelto", et: "Resuelto" },
  { clave: "cerrado", et: "Cerrado" },
];

const PRIORIDADES = [
  { clave: "baja", et: "Baja" },
  { clave: "normal", et: "Normal" },
  { clave: "alta", et: "Alta" },
  { clave: "urgente", et: "Urgente" },
];

const ABIERTOS = ["abierto", "en_curso", "esperando"];

const etiquetaDe = (lista, clave) =>
  lista.find((x) => x.clave === clave)?.et || clave;

/* Fechas cortas: "hace 20 min" dice más que un timestamp cuando lo que se
 * quiere saber es si alguien lo miró hoy. Más allá de una semana la fecha
 * exacta vuelve a ser lo útil. */
function cuando(iso) {
  const t = new Date(iso);
  const min = Math.floor((Date.now() - t.getTime()) / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  if (min < 60 * 24) return `hace ${Math.floor(min / 60)} h`;
  if (min < 60 * 24 * 7) return `hace ${Math.floor(min / 1440)} d`;
  return diaAr(iso); // en día argentino, no en el de la máquina
}

/* EL RECLAMO DE UNA SESIÓN (`claude:<sesión>`, PROTOCOLO §0 de FlotaBot,
 * 17/10/2026). Antes de tocar un ticket, la sesión que lo va a hacer lo
 * reclama escribiendo eso en `asignado_a`, la misma columna donde una persona
 * tiene su mail. No es el mail de nadie —no cuenta como «A mi cargo» ni como
 * «Sin dueño»: lo está haciendo el sistema— y ante la gente el sistema es
 * Cepi, nunca «claude». */
const esReclamo = (v) => /^claude:/i.test((v || "").trim());
const nombreCorto = (mail) =>
  esReclamo(mail) ? "Cepi" : (mail || "").split("@")[0] || "—";

/* LAS CONSULTAS DE LOS CHOFERES (`origen = 'chofer_recibo'`). Las abre
 * FlotaBot cuando un chofer pregunta por su recibo de sueldo, y se le contesta
 * desde acá: la respuesta le llega por WhatsApp en la próxima pasada del bot.
 * El origen lo pone sólo el bot (las funciones de alta del bot son de
 * service_role), así que un ticket del hub no puede hacerse pasar por uno. */
const ORIGEN_CHOFER = "chofer_recibo";
const esDeChofer = (t) => t?.origen === ORIGEN_CHOFER;

/* Lo que entra en la plantilla de WhatsApp cuando la ventana de 24 h está
 * cerrada. El mismo tope está en `responder_ticket_chofer`. */
const TOPE_RESPUESTA = 700;

/* El chip de la categoría. La que no está en la lista se muestra cruda: una
 * categoría que no reconozco sigue siendo un dato. */
const CATEGORIAS = { recibo_sueldo: "Recibo de sueldo" };
const etiquetaCategoria = (c) => (c ? CATEGORIAS[c] || c : "");

/* Quién lo abrió, para la línea de arriba. Un chofer escribe desde WhatsApp y
 * en `abierto_por` queda su número: se muestra su nombre, y el número no se
 * dibuja, tenga quien mira la llave que tenga. */
const quienAbrio = (t) =>
  esDeChofer(t) ? t.abierto_por_nombre || "un chofer" : nombreCorto(t.abierto_por);

/* Quién escribió un renglón del hilo. EL NÚMERO TAMPOCO SE DIBUJA ACÁ: las
 * funciones con las que el bot abre el ticket firman el «abrió» y el «se lo
 * asignó a» con el mismo `whatsapp:<número>` de `abierto_por`. El «abrió» es
 * del chofer; la asignación no la eligió él, la puso el bot según la tabla de
 * destinos. */
const autorDelRenglon = (t, m) => {
  if (esDeChofer(t) && /^whatsapp:/i.test(m?.autor || ""))
    return m?.cambio?.asignado ? "FlotaBot" : quienAbrio(t);
  return nombreCorto(m?.autor);
};

/* El detalle con sus links tocables. Los tickets que abre el «Derivar a…» de
 * Avisos de ruta (Igna, 06/10) traen el link a la foto y a la novedad en el
 * texto; sin esto había que copiarlos a mano. Sólo http(s): lo que se pinta
 * como link no puede ser un `javascript:`. */
const RE_LINK = /(https?:\/\/[^\s<>"']+)/g;
function conLinks(texto) {
  return String(texto || "")
    .split(RE_LINK)
    .map((parte, i) =>
      i % 2 === 1 ? (
        <a key={i} href={parte} target="_blank" rel="noopener noreferrer">
          {parte}
        </a>
      ) : (
        parte
      )
    );
}

/* Lo que dice un renglón de cambio. Se arma acá y no se guarda como texto en la
 * base para que un cambio de redacción no obligue a reescribir el historial. */
function contarCambio(c) {
  if (!c) return null;
  if (c.abrio) return "abrió el ticket";
  if (c.al_chofer) return "le respondió al chofer:";
  if (c.a) return `pasó de ${etiquetaDe(ESTADOS, c.de)} a ${etiquetaDe(ESTADOS, c.a)}`;
  if (c.asignado) return c.asignado === "—" ? "lo dejó sin asignar" : `se lo asignó a ${nombreCorto(c.asignado)}`;
  if (c.prioridad_a)
    return `cambió la prioridad de ${etiquetaDe(PRIORIDADES, c.prioridad_de)} a ${etiquetaDe(PRIORIDADES, c.prioridad_a)}`;
  return "hizo un cambio";
}

// --------------------------------------------------------------------- alta
const Nuevo = ({ areas, onListo, onCancelar }) => {
  const [titulo, setTitulo] = useState("");
  const [area, setArea] = useState(areas[0]?.clave || "");
  const [detalle, setDetalle] = useState("");
  const [prioridad, setPrioridad] = useState("normal");
  const [sobre, setSobre] = useState("");
  const [error, setError] = useState(null);
  const [mandando, setMandando] = useState(false);

  const mandar = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) return setError("Falta decir qué pasa.");
    setMandando(true);
    setError(null);
    try {
      const id = await abrirTicket({ titulo, area, detalle, prioridad, sobre });
      onListo(id);
    } catch (err) {
      setError(err.message || "No se pudo abrir el ticket");
      setMandando(false);
    }
  };

  return (
    <form className="tk-nuevo" onSubmit={mandar}>
      <h2>Nuevo ticket</h2>

      <label className="tk-campo">
        <span>Qué pasa</span>
        <input
          autoFocus
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          maxLength={200}
          placeholder="Se rompió la barrera de entrada"
        />
      </label>

      <div className="tk-campos">
        <label className="tk-campo">
          <span>Área</span>
          <select value={area} onChange={(e) => setArea(e.target.value)}>
            {areas.map((a) => (
              <option key={a.clave} value={a.clave}>
                {a.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <label className="tk-campo">
          <span>Prioridad</span>
          <select
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value)}
          >
            {PRIORIDADES.map((p) => (
              <option key={p.clave} value={p.clave}>
                {p.et}
              </option>
            ))}
          </select>
        </label>

        <label className="tk-campo">
          {/* Texto libre: exigir que apunte a una patente cargada haría que la
              mitad de los pedidos no se puedan escribir. */}
          <span>Sobre qué (opcional)</span>
          <input
            value={sobre}
            onChange={(e) => setSobre(e.target.value)}
            placeholder="Patente, legajo, depósito…"
          />
        </label>
      </div>

      <label className="tk-campo">
        <span>Detalle (opcional)</span>
        <textarea
          rows={4}
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
          placeholder="Todo lo que ayude a resolverlo sin tener que preguntar."
        />
      </label>

      {error && (
        <div className="hub-aviso error" role="status">
          {error}
        </div>
      )}

      <div className="tk-acciones">
        <button className="tk-boton tk-boton--fuerte" disabled={mandando}>
          {mandando ? "Abriendo…" : "Abrir ticket"}
        </button>
        <button type="button" className="tk-boton" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
};

// ------------------------------------------------------- responder al chofer
/* Lo que se escribe acá le llega al chofer por WhatsApp, tal cual. Abajo dice
 * en qué quedó la última respuesta: si ya le llegó, si sale en un rato, o si no
 * le pudo llegar — y en ese caso qué hacer. «No le llegó» nunca se dibuja como
 * «enviada». */
function estadoRespuesta(t) {
  if (!t.respuesta) return null;
  if (t.aviso_ok === true)
    return { clase: "ok", texto: `Le llegó al chofer ${cuando(t.avisado_en)}.` };
  if (t.aviso_ok === false)
    return {
      clase: "error",
      texto:
        "No le pudo llegar por WhatsApp" +
        (t.aviso_error ? ` (${t.aviso_error})` : "") +
        ". Probá escribirle o llamarlo por otro lado.",
    };
  // El bot la marca antes de mandarla y anota el resultado después: con la
  // marca y sin resultado, salió y no hay confirmación de que llegó.
  if (t.avisado_en)
    return {
      clase: "alerta",
      texto: `Salió hacia el chofer ${cuando(t.avisado_en)}, sin confirmación de que le llegó.`,
    };
  return { clase: "alerta", texto: "Sale por WhatsApp en los próximos minutos." };
}

const ResponderAlChofer = ({ ticket, ocupado, hacer }) => {
  const [texto, setTexto] = useState("");
  const estado = estadoRespuesta(ticket);

  const mandar = (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    const cuerpo = texto;
    hacer(() => responderAlChofer(ticket.id, cuerpo)).then((ok) => ok && setTexto(""));
  };

  return (
    <form className="tk-al-chofer" onSubmit={mandar}>
      <label className="tk-campo">
        <span>{ticket.respuesta ? "Mandarle otra respuesta al chofer" : "Responder al chofer"}</span>
        <textarea
          rows={3}
          value={texto}
          maxLength={TOPE_RESPUESTA}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Respuesta al chofer"
          placeholder="Le llega por WhatsApp tal cual la escribas."
        />
      </label>
      <div className="tk-acciones">
        <button className="tk-boton tk-boton--fuerte" disabled={ocupado || !texto.trim()}>
          Mandar al chofer
        </button>
        <span className="tk-cuando">
          {texto.length}/{TOPE_RESPUESTA}
        </span>
      </div>
      {estado && (
        <div className={"hub-aviso " + estado.clase} role="status">
          {estado.texto}
        </div>
      )}
    </form>
  );
};

// ------------------------------------------------------------------ detalle
const Detalle = ({ ticket, areas, permisos, yo, onCambio }) => {
  const [hilo, setHilo] = useState(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const cargarHilo = useCallback(
    () => traerHilo(ticket.id).then(setHilo).catch(() => setHilo([])),
    [ticket.id]
  );

  /* EL WHATSAPP DEL CHOFER, si el ticket es un aviso de ruta derivado. Nico,
     09/10: «en cualquiera que venga de parte de un chofer […] que aparezca el
     número del chofer clickeable». El «Derivar a…» le abre ticket a quien
     tiene área (taller, furgones) y tarea a quien no; en Mis pendientes el
     número ya está, y acá tiene que estar igual: es el mismo reporte. Sale
     del aviso (`avisos_ruta.numero`, el WhatsApp desde el que escribió), sólo
     con `ver.telefono_chofer` —sin la llave ni se consulta— y sólo si el
     aviso es de un chofer. */
  const verTel = puedeVerTelefono(permisos);
  const refAviso = avisoDelTicket(ticket);
  const [chofer, setChofer] = useState(null); // { ref, numero, quien, rol }
  useEffect(() => {
    // Sin la llave, ni la consulta ni lo que haya quedado de antes.
    if (!verTel || !refAviso) {
      setChofer(null);
      return;
    }
    let vigente = true;
    choferesDeAvisos([refAviso])
      .then((m) => vigente && setChofer({ ...(m || {})[refAviso], ref: refAviso }))
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [verTel, refAviso]);
  // Se vuelve a mirar todo en cada render: si la llave se fue («Ver como»
  // otra persona) o la respuesta es de otro aviso, no se dibuja.
  const delChofer =
    verTel &&
    chofer?.ref === refAviso &&
    esChofer(chofer.rol) &&
    whatsappDeChofer(chofer.numero, permisos)
      ? chofer
      : null;

  useEffect(() => {
    setHilo(null);
    setTexto("");
    setError(null);
    cargarHilo();
  }, [cargarHilo]);

  const area = areas.find((a) => a.clave === ticket.area);
  /* Se calcula acá para saber qué botones dibujar, pero quien decide de verdad
     es la base, que lo verifica en cada llamada. Si esto se equivoca lo peor
     que pasa es que un botón dé error, no que alguien haga lo que no debe. */
  const puedoAtender =
    permisos === null ||
    (ticket.asignado_a || "").toLowerCase() === yo ||
    permisos.includes("admin.permisos") ||
    (area && permisos.includes(area.atender_permiso));
  const loAbriYo = (ticket.abierto_por || "").toLowerCase() === yo;

  /* Devuelve si salió: la caja del chofer limpia el texto sólo entonces, y
     una respuesta que la base rechazó queda escrita para corregirla. */
  const hacer = async (fn) => {
    setOcupado(true);
    setError(null);
    try {
      await fn();
      await Promise.all([cargarHilo(), onCambio()]);
      return true;
    } catch (e) {
      setError(e.message || "No se pudo");
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const comentar = (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    const cuerpo = texto;
    setTexto("");
    hacer(() => comentarTicket(ticket.id, cuerpo));
  };

  return (
    <section className="tk-detalle">
      <div className="tk-cabeza">
        <div className={"tk-prio tk-prio--" + ticket.prioridad} />
        <div style={{ minWidth: 0 }}>
          <h2>{ticket.titulo}</h2>
          <p className="tk-meta">
            #{ticket.id} · {area?.etiqueta || ticket.area} ·{" "}
            {quienAbrio(ticket)} {cuando(ticket.creado_en)}
            {ticket.sobre && <> · sobre {ticket.sobre}</>}
            {ticket.categoria && (
              <>
                {" "}
                <span className="tk-chip tk-chip--cat">{etiquetaCategoria(ticket.categoria)}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {ticket.detalle && <p className="tk-cuerpo">{conLinks(ticket.detalle)}</p>}

      {delChofer && (
        <p className="tk-meta">
          <WhatsappChofer
            numero={delChofer.numero} permisos={permisos} nombre={delChofer.quien}
            etiqueta="WhatsApp del chofer:"
          />
        </p>
      )}

      <div className="tk-barra">
        <span className="et">Estado:</span>
        {ESTADOS.map((e) => {
          // Quien lo abrió puede cerrarlo aunque no sea de su área: si el que
          // pidió dice que ya está, no hace falta que un tercero lo confirme.
          const habilitado =
            puedoAtender || (loAbriYo && ["resuelto", "cerrado"].includes(e.clave));
          return (
            <button
              key={e.clave}
              className={
                "tk-chip" + (ticket.estado === e.clave ? " activo" : "")
              }
              disabled={ocupado || !habilitado || ticket.estado === e.clave}
              onClick={() => hacer(() => moverTicket(ticket.id, e.clave))}
            >
              {e.et}
            </button>
          );
        })}
      </div>

      {puedoAtender && (
        <div className="tk-barra">
          <span className="et">Prioridad:</span>
          {PRIORIDADES.map((p) => (
            <button
              key={p.clave}
              className={
                "tk-chip" + (ticket.prioridad === p.clave ? " activo" : "")
              }
              disabled={ocupado || ticket.prioridad === p.clave}
              onClick={() => hacer(() => priorizarTicket(ticket.id, p.clave))}
            >
              {p.et}
            </button>
          ))}
        </div>
      )}

      {puedoAtender && (
        <div className="tk-barra">
          <span className="et">A cargo:</span>
          <span className="tk-quien">
            {ticket.asignado_a ? nombreCorto(ticket.asignado_a) : "nadie"}
          </span>
          {(ticket.asignado_a || "").toLowerCase() !== yo && (
            <button
              className="tk-chip"
              disabled={ocupado}
              onClick={() => hacer(() => asignarTicket(ticket.id, yo))}
            >
              Lo tomo yo
            </button>
          )}
          {ticket.asignado_a && (
            <button
              className="tk-chip"
              disabled={ocupado}
              onClick={() => hacer(() => asignarTicket(ticket.id, null))}
            >
              Soltar
            </button>
          )}
        </div>
      )}

      {esDeChofer(ticket) && puedoAtender && (
        <ResponderAlChofer ticket={ticket} ocupado={ocupado} hacer={hacer} />
      )}

      {error && (
        <div className="hub-aviso error" role="status">
          {error}
        </div>
      )}

      <div className="tk-hilo">
        {hilo === null && <p className="tk-vacio">Cargando…</p>}
        {hilo?.map((m) => (
          <div
            key={m.id}
            // La respuesta al chofer trae `cambio` para marcarla, pero es un
            // texto que alguien escribió: se lee como un mensaje, no como un
            // cambio de estado.
            className={"tk-msg" + (m.cambio && !m.cambio.al_chofer ? " tk-msg--cambio" : "")}
          >
            <span className="tk-autor">{autorDelRenglon(ticket, m)}</span>
            {m.cambio?.al_chofer ? (
              <span className="tk-texto">
                <span className="tk-cambio">{contarCambio(m.cambio)}</span> {m.cuerpo}
              </span>
            ) : m.cambio ? (
              <span className="tk-cambio">{contarCambio(m.cambio)}</span>
            ) : (
              <span className="tk-texto">{m.cuerpo}</span>
            )}
            <span className="tk-cuando">{cuando(m.creado_en)}</span>
          </div>
        ))}
      </div>

      <form className="tk-responder" onSubmit={comentar}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribir algo…"
          aria-label="Escribir un comentario"
        />
        <button className="tk-boton" disabled={ocupado || !texto.trim()}>
          Enviar
        </button>
      </form>
    </section>
  );
};

// -------------------------------------------------------------------- lista
const Tickets = () => {
  const { user } = useAuth();
  const yo = (user?.email || "").toLowerCase();

  const [tickets, setTickets] = useState(null);
  const [areas, setAreas] = useState([]);
  const [miArea, setMiArea] = useState(null);
  /* DEL ARMAZÓN Y NO DE UNA CONSULTA PROPIA. Los pedía con `misPermisos()`,
   * que devuelve los de la sesión REAL — así que «Ver como» no llegaba hasta
   * acá y la pantalla seguía ofreciendo «Atender» sobre áreas que la persona
   * previsualizada no puede atender. Una vista previa que dice que sí donde el
   * sistema va a decir que no es peor que no tenerla. */
  const { permisos = null } = useOutletContext() || {};
  const [error, setError] = useState(null);
  const [elegido, setElegido] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [filtro, setFiltro] = useState("pendientes");
  const [busqueda, setBusqueda] = useState("");

  /* EL TICKET QUE PIDIÓ EL BUSCADOR. Igna, 02/10: «que se abra en el ítem
   * exacto, no solo la pantalla». Con `?ver=<id>` se abre ese ticket como si
   * se lo hubiera tocado en la lista: se elige, se le saca el filtro que lo
   * escondía (un cerrado no está en «Sin resolver») y el renglón titila.
   *
   * Si no vino en los 500 de la lista —uno viejo, cerrado hace meses— se pide
   * solo, por id, y se suma aparte (`suelto`). Se relee en cada `cargar` para
   * que un cambio hecho desde el detalle se vea igual que en los demás. Si la
   * base no lo devuelve (no existe, o no es de los que podés ver) se dice, y
   * no se abre nada. */
  const { ver, resaltar, listo } = useItemPedido();
  const [suelto, setSuelto] = useState(null);
  const sueltoId = useRef(null);
  const buscado = useRef("");
  const [avisoPedido, setAvisoPedido] = useState("");

  const cargar = useCallback(() => {
    const id = sueltoId.current;
    return Promise.all([
      traerTickets(),
      id ? traerTicket(id).catch(() => null) : null,
    ])
      .then(([lista, uno]) => {
        setTickets(lista);
        if (id) setSuelto(uno);
      })
      .catch((e) => setError(e.message || "No se pudieron traer los tickets"));
  }, []);

  useEffect(() => {
    cargar();
    traerAreas().then(setAreas).catch(() => setAreas([]));
    Promise.resolve()
      .then(traerMiArea)
      .then(setMiArea)
      .catch(() => setMiArea(null));
  }, [cargar]);

  const FILTROS = [
    { clave: "pendientes", et: "Sin resolver", f: (t) => ABIERTOS.includes(t.estado) },
    { clave: "mios", et: "A mi cargo", f: (t) => (t.asignado_a || "").toLowerCase() === yo },
    { clave: "pedi", et: "Los que pedí", f: (t) => (t.abierto_por || "").toLowerCase() === yo },
    { clave: "sueltos", et: "Sin dueño", f: (t) => !t.asignado_a && ABIERTOS.includes(t.estado) },
    { clave: "todos", et: "Todos", f: () => true },
  ];

  // La lista más el pedido suelto, si no estaba ya entre los 500.
  const todos = useMemo(
    () =>
      !tickets || !suelto || tickets.some((t) => t.id === suelto.id)
        ? tickets
        : [...tickets, suelto],
    [tickets, suelto]
  );

  const visibles = useMemo(() => {
    const f = FILTROS.find((x) => x.clave === filtro)?.f || (() => true);
    const q = busqueda.trim().toLowerCase();
    return (todos || []).filter(
      (t) =>
        f(t) &&
        (laVeo(t, yo, miArea) || t.id === elegido) &&
        (!q ||
          t.titulo.toLowerCase().includes(q) ||
          (t.sobre || "").toLowerCase().includes(q) ||
          String(t.id) === q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos, filtro, busqueda, yo, miArea, elegido]);

  // El ticket abierto se relee de la lista y no se guarda aparte: así después
  // de un cambio la cabecera y el renglón dicen lo mismo sin sincronizar nada.
  const ticket = (todos || []).find((t) => t.id === elegido) || null;

  // El pedido del buscador: encontrarlo (o ir a buscarlo) y abrirlo.
  useEffect(() => {
    if (!ver) buscado.current = "";
    if (!ver || !todos) return;
    const t = todos.find((x) => String(x.id) === ver);
    if (t) {
      setAvisoPedido("");
      setElegido(t.id);
      setNuevo(false);
      setBusqueda("");
      const f = FILTROS.find((x) => x.clave === filtro)?.f;
      if (f && !f(t)) setFiltro("todos");
      return;
    }
    if (buscado.current === ver) return; // ya se pidió; espera la respuesta
    buscado.current = ver;
    const noEsta = () => {
      setAvisoPedido("No encontré ese ticket.");
      listo();
    };
    traerTicket(ver)
      .then((uno) => {
        if (!uno) return noEsta();
        sueltoId.current = uno.id;
        setSuelto(uno);
      })
      .catch(noEsta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ver, todos]);

  // Ya abierto y con el renglón dibujado: titila, y el `?ver=` se va.
  useEffect(() => {
    if (!ver || String(elegido) !== ver) return;
    resaltar(buscarFila(ver));
    listo();
  }, [ver, elegido, filtro, resaltar, listo]);

  if (error && !tickets) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!tickets) return <Loading />;

  return (
    <div className="tk">
      <aside className="tk-lista">
        <div className="tk-arriba">
          <button
            className="tk-boton tk-boton--fuerte"
            onClick={() => {
              setNuevo(true);
              setElegido(null);
            }}
          >
            + Nuevo ticket
          </button>
        </div>

        <input
          className="permisos-buscar"
          type="search"
          placeholder="Buscar…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label="Buscar ticket"
        />

        <div className="tk-filtros">
          {FILTROS.map((f) => (
            <button
              key={f.clave}
              className={"tk-chip" + (filtro === f.clave ? " activo" : "")}
              onClick={() => setFiltro(f.clave)}
            >
              {f.et}
            </button>
          ))}
        </div>

        {avisoPedido && (
          <div className="hub-aviso alerta" role="status">
            {avisoPedido}
          </div>
        )}

        {visibles.map((t) => (
          <button
            key={t.id}
            data-ver={t.id}
            className={"tk-item" + (t.id === elegido ? " activa" : "")}
            onClick={() => {
              setElegido(t.id);
              setNuevo(false);
            }}
          >
            <span className={"tk-prio tk-prio--" + t.prioridad} />
            <span className="tk-item-txt">
              <span className="tk-item-tit">{t.titulo}</span>
              <span className="tk-item-meta">
                {t.categoria && <>{etiquetaCategoria(t.categoria)} · </>}
                {etiquetaDe(ESTADOS, t.estado)} ·{" "}
                {areas.find((a) => a.clave === t.area)?.etiqueta || t.area} ·{" "}
                {t.asignado_a ? nombreCorto(t.asignado_a) : "sin dueño"} ·{" "}
                {cuando(t.movido_en)}
              </span>
            </span>
          </button>
        ))}

        {visibles.length === 0 && (
          <p className="tk-vacio">
            {tickets.length === 0
              ? "Todavía no hay tickets. El primero lo abrís vos."
              : "Nada con ese filtro."}
          </p>
        )}
      </aside>

      {nuevo ? (
        <Nuevo
          areas={areas}
          onCancelar={() => setNuevo(false)}
          onListo={async (id) => {
            setNuevo(false);
            await cargar();
            setElegido(id);
            // Un ticket recién abierto casi nunca cae en "Sin resolver" a la
            // vista si el filtro estaba en otro lado: mostrarlo evita el
            // "¿se guardó?" de los primeros diez segundos.
            setFiltro("pendientes");
          }}
        />
      ) : ticket ? (
        <Detalle
          key={ticket.id}
          ticket={ticket}
          areas={areas}
          permisos={permisos}
          yo={yo}
          onCambio={cargar}
        />
      ) : (
        <section className="tk-detalle tk-detalle--vacio">
          <p>Elegí un ticket de la izquierda, o abrí uno nuevo.</p>
        </section>
      )}
    </div>
  );
};

export default Tickets;
